import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import Parse from 'parse';
import { useAuth } from './AuthContext';

export const ApprovalPage = () => {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role');

  // Approvers click this straight from their email, often in a browser where
  // they've never signed in. Without a way to sign in here, Approve/Deny just
  // errored with "you must be logged in". So the page can sign them in itself.
  const { user, signIn, authError } = useAuth();
  const isSignedIn = !!(user || Parse.User.current());

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionComplete, setActionComplete] = useState(false);

  useEffect(() => {
    loadFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const loadFormData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formId || !role) {
        throw new Error('Invalid approval link. Form ID or role is missing.');
      }

      // Get form details and approval status
      const response = await Parse.Cloud.run('getFormApprovalStatus', { formId });

      if (!response.success) {
        throw new Error(response.message || 'Failed to load form details');
      }

      setForm(response);
    } catch (error) {
      console.error('Error loading form:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const userEmail = Parse.User.current()?.get('email');
      if (!userEmail) {
        throw new Error('You must be logged in to approve forms');
      }

      const response = await Parse.Cloud.run('approveForm', {
        formId,
        userEmail,
        role
      });

      if (!response.success) {
        throw new Error(response.message);
      }

      setActionComplete(true);
      await loadFormData(); // Refresh status
    } catch (error) {
      console.error('Error approving form:', error);
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    try {
      if (!reason.trim()) {
        throw new Error('Please provide a reason for denying this request');
      }

      setSubmitting(true);
      setError(null);

      const userEmail = Parse.User.current()?.get('email');
      if (!userEmail) {
        throw new Error('You must be logged in to deny forms');
      }

      const response = await Parse.Cloud.run('denyForm', {
        formId,
        userEmail,
        role,
        reason: reason.trim()
      });

      if (!response.success) {
        throw new Error(response.message);
      }

      setActionComplete(true);
      await loadFormData(); // Refresh status
    } catch (error) {
      console.error('Error denying form:', error);
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-center text-gray-600 mt-4">Loading approval details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <span className="text-red-600 text-2xl">⚠️</span>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Error</h3>
            <p className="mt-2 text-sm text-gray-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900">Form Not Found</h3>
            <p className="mt-2 text-sm text-gray-500">The requested form could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if already processed
  const isAlreadyApprovedByRole = form.completedApprovals?.includes(role);
  const isDenied = form.isDenied;
  const isFullyApproved = form.isFullyApproved;

  if (actionComplete || isAlreadyApprovedByRole || isDenied || isFullyApproved) {
    let statusMessage, statusIcon, statusColor;

    if (isDenied) {
      statusMessage = 'This event request has been denied';
      statusIcon = '❌';
      statusColor = 'text-red-600';
    } else if (isFullyApproved) {
      statusMessage = 'This event request has been fully approved';
      statusIcon = '✅';
      statusColor = 'text-green-600';
    } else if (isAlreadyApprovedByRole) {
      statusMessage = `This request has already been approved by ${role}`;
      statusIcon = '✅';
      statusColor = 'text-green-600';
    } else {
      statusMessage = 'Action completed successfully';
      statusIcon = '✅';
      statusColor = 'text-green-600';
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100">
              <span className="text-4xl">{statusIcon}</span>
            </div>
            <h3 className={`mt-4 text-xl font-medium ${statusColor}`}>{statusMessage}</h3>

            {form.formData && (
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Event Details</h4>
                <div className="text-left space-y-2 text-sm">
                  <div><span className="font-medium">Event:</span> {form.formData.eventName || 'N/A'}</div>
                  <div><span className="font-medium">Department:</span> {form.formData.department || 'N/A'}</div>
                  <div><span className="font-medium">Date:</span> {form.formData.dayDateOfEvent || 'N/A'}</div>
                  <div><span className="font-medium">Location:</span> {form.formData.locationRequested || 'N/A'}</div>
                </div>
              </div>
            )}

            <p className="mt-4 text-sm text-gray-500">
              You can close this window and return to your email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-8">
            <h1 className="text-2xl font-bold text-white">Event Approval Required</h1>
            <p className="text-blue-100 mt-2">Approval requested for role: <span className="font-semibold">{role}</span></p>
          </div>

          {/* Form Details */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Event Information</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex">
                    <span className="w-24 font-medium text-gray-600">Event:</span>
                    <span className="text-gray-900">{form.formData?.eventName || 'N/A'}</span>
                  </div>
                  <div className="flex">
                    <span className="w-24 font-medium text-gray-600">Department:</span>
                    <span className="text-gray-900">{form.formData?.department || 'N/A'}</span>
                  </div>
                  <div className="flex">
                    <span className="w-24 font-medium text-gray-600">Date:</span>
                    <span className="text-gray-900">{form.formData?.dayDateOfEvent || 'N/A'}</span>
                  </div>
                  <div className="flex">
                    <span className="w-24 font-medium text-gray-600">Time:</span>
                    <span className="text-gray-900">
                      {form.formData?.startTime && form.formData?.endTime
                        ? `${form.formData.startTime} - ${form.formData.endTime}`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-24 font-medium text-gray-600">Location:</span>
                    <span className="text-gray-900">{form.formData?.locationRequested || 'N/A'}</span>
                  </div>
                  <div className="flex">
                    <span className="w-24 font-medium text-gray-600">Organizer:</span>
                    <span className="text-gray-900">{form.formData?.eventOrganizer || form.formData?.ownerEmail || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Approval Status</h3>
                <div className="space-y-3">
                  <div className="text-sm">
                    <span className="font-medium text-gray-600">Required Approvals:</span>
                    <div className="mt-1">
                      {form.requiredApprovals?.map(reqRole => (
                        <div key={reqRole} className="flex items-center space-x-2">
                          <span className={`text-sm ${form.completedApprovals?.includes(reqRole) ? 'text-green-600' : 'text-gray-500'}`}>
                            {form.completedApprovals?.includes(reqRole) ? '✅' : '⏳'}
                          </span>
                          <span className={`text-sm ${form.completedApprovals?.includes(reqRole) ? 'text-green-600 font-medium' : 'text-gray-700'}`}>
                            {reqRole}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-red-400">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="mt-2 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 border-t pt-6">
              {!isSignedIn ? (
                <div className="rounded-lg bg-gray-50 border border-gray-200 p-6 text-center">
                  <h3 className="text-lg font-medium text-gray-900">Sign in to approve or deny</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Use your Bethel Google account. We need to know who approved this.
                  </p>
                  <div className="mt-4 flex justify-center">
                    <GoogleLogin
                      onSuccess={(response) => signIn(response)}
                      onError={() => setError('Sign-in failed. Please try again.')}
                    />
                  </div>
                  {authError && <p className="mt-3 text-sm text-red-600">{authError}</p>}
                </div>
              ) : (
                <div className="flex flex-col space-y-4">
                  <div>
                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Denial (optional for approval, required for denial):
                    </label>
                    <textarea
                      id="reason"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter reason if denying this request..."
                    />
                  </div>

                  <p className="text-sm text-gray-500">Signed in as {user?.email || Parse.User.current()?.get('email')}</p>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={handleApprove}
                      disabled={submitting}
                      className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium py-3 px-6 rounded-lg transition duration-200"
                    >
                      {submitting ? 'Processing...' : '✅ Approve Event'}
                    </button>

                    <button
                      onClick={handleDeny}
                      disabled={submitting || !reason.trim()}
                      className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium py-3 px-6 rounded-lg transition duration-200"
                    >
                      {submitting ? 'Processing...' : '❌ Deny Event'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};